/** @jsxImportSource @emotion/react */
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import React, { useEffect, useMemo, useReducer, useRef } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { FloatButton } from 'antd'
import { MobileOutlined, DesktopOutlined } from '@ant-design/icons'
import Helmet from '../../components/Helmet'
import { EventCard } from '../../components/RegistrationEventCard'
import { EventProgress } from '../../components/RegistrationEventProgress'
import useWindowSize from '../../hooks/useWindowSize'
import EmptyRegistration from '../RegistrationInviteV2/EmptyRegistration'
import LoadingRegistration from '../RegistrationInviteV2/LoadingRegistration'
import hockeyDefaultBg from '../RegistrationInviteV2/steps/hockey_default_bg.png'
import { PageTitle } from '../RegistrationInviteV2/steps/Main'
import colors from '@sportninja/common/constants/appColors'
import SelectedDivisionCard from './components/SelectedDivisionCard'
import DynamicFormPreview from './components/DynamicFormPreview'
import PreviewBanner from './components/PreviewBanner'
import { FormType } from '../../types/CustomForm'
import {
  getBackgroundImage,
  findSportById,
  isSoccerSport,
  isGenericSport as checkIsGenericSport,
  findGenericSportById,
  getSelectedForm,
  type GenericSport,
  type Sport,
} from './utils'
import {
  registrationPreviewReducer,
  initialState,
} from './registrationPreviewReducer'
import {
  Container,
  createBgImageWrapper,
  HiddenImage,
  ContentBox,
  globalStyles,
} from './styles'
import { PreviewProvider } from './PreviewContext'

interface RegistrationPreviewProps {
  genericSportProperties: GenericSport[]
  playerTypes: any
  sports: Sport[]
}

/**
 * Handles image error by setting fallback background
 * @param divRef - Reference to the background div element
 */
const handleImageError = (divRef: React.RefObject<HTMLDivElement>): void => {
  if (divRef.current) {
    divRef.current.style.backgroundImage = `url(${hockeyDefaultBg})`
  }
}

const RegistrationPreview: React.FC<RegistrationPreviewProps> = ({
  genericSportProperties,
  playerTypes: _playerTypes,
  sports,
}) => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const {
    data: registrationData,
    loading,
    error,
    refetch,
  } = useRegistrationData(id)
  const { width, height } = useWindowSize()
  const divRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (id) {
      refetch()
    }
  }, [id])

  // Initialize reducer state
  const [state, dispatch] = useReducer(registrationPreviewReducer, initialState)

  // Extract form_type from URL query parameters
  const formType: FormType = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const type = searchParams.get('form_type')
    return type === 'player' ? 'player' : 'team' // Default to 'team'
  }, [location.search])

  // Sync registration data with reducer
  useEffect(() => {
    if (loading) {
      dispatch({ type: 'FETCH_START' })
    } else if (error) {
      dispatch({ type: 'FETCH_ERROR', payload: error })
    } else if (registrationData?.detailed) {
      dispatch({ type: 'FETCH_SUCCESS', payload: registrationData.detailed })
    }
  }, [loading, error, registrationData])

  // Sync window size with reducer (only when in desktop preview mode)
  useEffect(() => {
    if (state.previewMode === 'desktop') {
      dispatch({ type: 'WINDOW_RESIZE', payload: { width, height } })
    }
  }, [width, height, state.previewMode])

  // Calculate derived values
  const sport = useMemo(
    () =>
      findSportById(sports, state.registration?.registration?.sport_id || ''),
    [sports, state.registration?.registration?.sport_id]
  )

  const isSoccer = useMemo(() => isSoccerSport(sport || null), [sport])

  const isGenericSportUsed = useMemo(
    () => checkIsGenericSport(state.registration),
    [state.registration]
  )

  const genericSport = useMemo(() => {
    if (
      !isGenericSportUsed ||
      !state.registration?.registration?.root_schedule?.generic_sport_id
    ) {
      return null
    }
    return findGenericSportById(
      genericSportProperties,
      state.registration.registration.root_schedule.generic_sport_id
    )
  }, [
    isGenericSportUsed,
    genericSportProperties,
    state.registration?.registration?.root_schedule?.generic_sport_id,
  ])

  // Calculate background image
  const backgroundImage = useMemo(() => {
    return getBackgroundImage(
      genericSport,
      isSoccer,
      'game_hockey',
      state.isDesktop,
      sport || null
    )
  }, [genericSport, isSoccer, state.isDesktop, sport])

  // Calculate API background image
  const apiBackgroundImage = useMemo(() => {
    return (
      state.registration?.registration?.image?.full_path ||
      `${backgroundImage}.png`
    )
  }, [state.registration?.registration?.image?.full_path, backgroundImage])

  // Update background image in state
  useEffect(() => {
    dispatch({ type: 'SET_BACKGROUND_IMAGE', payload: apiBackgroundImage })
  }, [apiBackgroundImage])

  // Get selected form based on form_type parameter
  const selectedForm = useMemo(() => {
    return getSelectedForm(state.registration, formType)
  }, [state.registration, formType])

  // Mock data for SelectedDivisionCard (to be replaced with real data later)
  const mockDivision = {
    name: 'Preview Division',
    division_age: { name: '99+' },
    division_gender: { name: 'Preview Gender' },
    team_cost: 999,
    player_cost: '999',
    deposit: 999,
    player_deposit: 999,
  }

  const mockCouponInfo = {
    id: 'coupon123',
    code: 'PREVIEW',
    discount: 99,
    type: 'percentage' as const,
    subtotal: 999,
    discount_amount: 50,
  }

  const mockCurrency = {
    name: 'CAD',
  }

  // Show loading state
  if (state.loading) {
    return <LoadingRegistration />
  }

  // Show error state
  if (state.error) {
    return <EmptyRegistration errorMessage={state.error.message} />
  }

  // Show empty state if no data
  if (!state.registration) {
    return (
      <EmptyRegistration
        title='Registration Not Found'
        errorMessage="The registration you're looking for could not be found."
      />
    )
  }

  // Show form not found state if form_type is specified but form doesn't exist
  if (!selectedForm) {
    return (
      <EmptyRegistration
        title='Form Not Found'
        errorMessage={`The ${formType} form for this registration could not be found or has no content.`}
      />
    )
  }

  console.log('state.registration', state.registration)

  return (
    <div css={Container}>
      <style>{globalStyles}</style>

      {/* Preview banner - shows on opposite side of toggle button */}
      <PreviewBanner id={id!} />

      {/* Force mobile styles when in mobile preview mode */}
      {state.previewMode === 'mobile' && (
        <style>
          {`
            /* Override all components within mobile preview to use mobile styles */
            .mobile-preview * {
              /* Reset any flex-direction that might be set to row */
            }
            
            /* Force EventCard to use mobile layout */
            .mobile-preview > *:nth-child(2) {
              flex-direction: column !important;
              align-items: center !important;
            }
            
            /* Force EventProgress to use mobile layout */  
            .mobile-preview > *:nth-child(3) {
              flex-direction: column !important;
              gap: 16px !important;
            }
            
            .mobile-preview > *:nth-child(3) > * {
              width: 100% !important;
              flex: none !important;
            }
            
            /* Force any grid to single column */
            .mobile-preview [style*="grid-template-columns: 1fr 1fr"],
            .mobile-preview [style*="grid-template-columns:1fr 1fr"] {
              grid-template-columns: 1fr !important;
            }
            
            /* Force flex containers to column on mobile */
            .mobile-preview [style*="display: flex"][style*="flex-direction: row"],
            .mobile-preview [style*="display:flex"][style*="flex-direction:row"] {
              flex-direction: column !important;
            }
          `}
        </style>
      )}

      <Helmet
        title={`Registration Preview - ${
          formType.charAt(0).toUpperCase() + formType.slice(1)
        } Form`}
      />

      {/* Preview mode toggle button */}
      <FloatButton
        tooltip={
          state.previewMode === 'desktop' ? 'Mobile preview' : 'Desktop preview'
        }
        icon={
          state.previewMode === 'desktop' ? (
            <MobileOutlined style={{ color: colors.PRIMARY }} />
          ) : (
            <DesktopOutlined style={{ color: colors.PRIMARY }} />
          )
        }
        onClick={() => dispatch({ type: 'TOGGLE_PREVIEW_MODE' })}
        style={{ position: 'fixed', top: 56, right: 24, zIndex: 1000 }}
      />

      {/* Background image section */}
      <div ref={divRef} css={createBgImageWrapper(state.backgroundImage)}>
        <img
          src={state.backgroundImage}
          onError={() => handleImageError(divRef)}
          css={HiddenImage}
          alt='Background'
        />
      </div>

      <PreviewProvider
        windowWidth={state.windowWidth}
        windowHeight={state.windowHeight}
        isDesktop={state.isDesktop}
        previewMode={state.previewMode}
      >
        <div
          css={ContentBox}
          className={state.previewMode === 'mobile' ? 'mobile-preview' : ''}
          style={
            state.previewMode === 'mobile'
              ? {
                  maxWidth: '375px',
                  margin: '0 auto',
                  border: '2px solid #1890ff',
                  boxShadow: '0 0 10px rgba(24, 144, 255, 0.3)',
                }
              : {}
          }
        >
          {/* Mobile preview indicator */}
          {state.previewMode === 'mobile' && (
            <div
              style={{
                background: '#1890ff',
                color: 'white',
                padding: '8px 16px',
                textAlign: 'center',
                marginBottom: '16px',
                borderRadius: '4px',
                fontWeight: 'bold',
              }}
            >
              📱 Mobile Preview Mode (375px width)
            </div>
          )}

          {/* Event information */}
          <EventCard eventInfo={state.registration as any} />

          {/* Static progress bar */}
          <EventProgress
            eventProgress={3}
            isWaiverRequired={state.registration.registration.show_waiver}
            isTeamRep={formType === 'team'}
          />

          {/* Page title */}
          <PageTitle
            title={`${
              formType.charAt(0).toUpperCase() + formType.slice(1)
            } Form Preview`}
            subtitle={`Here's how the ${formType} form will appear to registrants.`}
            showAlert={false}
            showSuccess={false}
          />

          {/* Selected Division Card */}
          <SelectedDivisionCard
            division={mockDivision}
            couponInfo={mockCouponInfo}
            isTeamRep={formType === 'team'}
            colors={colors}
            currency={mockCurrency}
          />

          {/* Dynamic Form Preview */}
          <DynamicFormPreview
            groups={selectedForm.groups}
            sportId={
              state.registration?.registration?.sport_id
                ? state.registration.registration.sport_id
                : undefined
            }
          />
        </div>
      </PreviewProvider>
    </div>
  )
}

export default RegistrationPreview
